import { useLayoutEffect, useRef, useState } from "react"
import { useLocation, useNavigationType } from "react-router-dom"
import { useManagedContext, useLista, useFilterPersistence, shouldRestoreFilters } from "../../components/hooks"
import { ServerRequest } from "../../utils/apiweb"
import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { APIS } from "../../config/apis"
import { SectionHeading, TableCustom, WrapperAdvancedSearch } from "../../components/common"
import { onRequestError, onSearchNoSuccess } from "../../utils/requests"
import { VALIDATION_MESSAGE } from "../../consts/validationMessage"
import { getDateToString, getFormatNumber, getNumeroRecibo_ObjectToIdentificador, getNumeroRecibo_IdentificadorToObject } from "../../utils/convert"
import ReciboPublicacionModal from "../../components/controls/ReciboPublicacionModal"


const setReturnState = (state) => {
    window.history.replaceState({ ...(window.history.state ?? {}), usr: state }, '')
}

const BusquedaReciboPublicacionView = () => {
    const { setIsLoading } = useManagedContext()
    const location = useLocation()
    const navigationType = useNavigationType()

    const searchRef = useRef()
    const tableRef = useRef()
    const isReturnNavigation = useRef(shouldRestoreFilters(location, navigationType, true))

    const [veps, setVeps] = useState([])
    const [filterErrors, setFilterErrors] = useState({})
    const [tableVisible, setTableVisible] = useState(false)
    const [searchMessage, setSearchMessage] = useState(null)
    const [applyRestoredPage, setApplyRestoredPage] = useState(true)
    const [selectedVep, setSelectedVep] = useState(() =>
        isReturnNavigation.current && location.state?.vepId
            ? { id: location.state.vepId }
            : null
    )

    const [getLista, ] = useLista({
        listas: ['TipoTributo'],
        onLoaded: (listas, isSuccess, error) => {
          if (!isSuccess) {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
          }
        },
        memo: {
          key: 'TipoTributo',
          timeout: 0
        }
    });

    const getDescTipoTributo = (codigoTipoTributo) => {
        const listaTipoTributo = getLista('TipoTributo');
        const tipoTributo = listaTipoTributo.find(item => item.codigo === codigoTipoTributo);
        return tipoTributo ? tipoTributo.nombre : "";
    }

    const isNumeroVepValido = (numeroVep) => {
        const normalized = numeroVep.replace(/-/g, '')
        return normalized.length >= 4 && /^\d+$/.test(normalized)
    }

    const validateFilters = ({ numeroVep }) => {
        if (numeroVep && !isNumeroVepValido(numeroVep)) {
            setFilterErrors({ numeroVep: 'Formato incorrecto' })
            return false
        }

        setFilterErrors({})
        return true
    }

    const SearchVepsWithFilters = (filters) => {
        const { numeroVep, codigoBarrasCliente, numeroCuenta, fechaDesde, fechaHasta } = filters
        let resolveSearch
        const searchDone = new Promise((resolve) => { resolveSearch = resolve })

        setIsLoading(true)

        setSearchMessage(null)
        setFilterErrors({})

        const params = new URLSearchParams()

        if (numeroVep) {
            if (!isNumeroVepValido(numeroVep)) {
                setVeps([])
                setTableVisible(false)
                setIsLoading(false)
                setFilterErrors({ numeroVep: 'Formato incorrecto' })
                return Promise.resolve(false)
            }
            const objectNumeroVep = getNumeroRecibo_IdentificadorToObject(numeroVep)
            params.append('codigoDelegacion', objectNumeroVep.codigoDelegacion)
            params.append('numeroRecibo', objectNumeroVep.numeroRecibo)
        }
        if (codigoBarrasCliente) params.append('codigoBarrasCliente', codigoBarrasCliente)
        if (numeroCuenta) params.append('numeroCuenta', numeroCuenta)
        if (fechaDesde) params.append('fechaDesde', fechaDesde.toISOString())
        if (fechaHasta) params.append('fechaHasta', fechaHasta.toISOString())

        const paramsUrl = `/recibo/filter?${params.toString()}`

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.RECIBO_PUBLICACION_LOTE,
            paramsUrl,
            null,
            res => res.json().then(data => {
                setVeps(data)
                setTableVisible(true)
                setIsLoading(false)
                resolveSearch(true)
            }),
            res => {
                onSearchNoSuccess(res, () => {
                    setVeps([])
                    setTableVisible(false)
                    setSearchMessage(VALIDATION_MESSAGE.TOO_MANY_RESULTS)
                })
                setIsLoading(false)
                resolveSearch(false)
            },
            err => {
                onRequestError(err)
                setIsLoading(false)
                resolveSearch(false)
            },
        )

        return searchDone
    }

    const { getCurrentFilters, restoredPageIndex, restoredGlobalFilter } = useFilterPersistence(
        searchRef,
        tableRef,
        (savedFilters) => {
            if (savedFilters.selectedValues?.length) SearchVepsWithFilters(savedFilters.filters)
        },
        { restoreOnPop: true }
    )

    useLayoutEffect(() => {
        if (isReturnNavigation.current) setReturnState(null)
    }, [])

    const SearchVeps = () => {
        setApplyRestoredPage(false)
        return SearchVepsWithFilters(searchRef.current.filters)
    }

    const prepareReturnToVep = () => {
        const returnState = { filters: getCurrentFilters(), vepId: selectedVep.id }
        setReturnState(returnState)
        return { volverAlVep: { to: '/busqueda/veps', state: returnState } }
    }

    const avmr = {
        onView: (item) => {
            setSelectedVep(item)
        }
    }

    return <>
        <SectionHeading titles={[{ title: 'VEPs' }, { title: 'Búsqueda de VEPs' }]} />

        <section className='section-accordion'>
            <WrapperAdvancedSearch
                message={searchMessage}
                ref={searchRef}
                requireFilter={true}
                errors={filterErrors}
                onValidation={validateFilters}
                filters={[
                    { title: 'N° VEP', field: 'numeroVep', type: 'string', size: 4, searchOnEnter: true },
                    { title: 'Código de barras', field: 'codigoBarrasCliente', type: 'string', size: 4, searchOnEnter: true },
                    { title: 'N° cuenta', field: 'numeroCuenta', type: 'string', size: 4, searchOnEnter: true },
                    { title: 'Fecha lote desde', field: 'fechaDesde', type: 'date', size: 4 },
                    { title: 'Fecha lote hasta', field: 'fechaHasta', type: 'date', size: 4, minValueField: 'fechaDesde' },
                ]}
                onSearch={SearchVeps}
                onClear={() => {
                    setVeps([])
                    setTableVisible(false)
                    setFilterErrors({})
                }}
            />

            {tableVisible && (
                <TableCustom
                    ref={tableRef}
                    className={'TableCustomBase m-top-20'}
                    data={veps}
                    avmr={avmr}
                    messageEmpty="No se encontraron resultados"
                    restoredPageIndex={applyRestoredPage ? restoredPageIndex : undefined}
                    defaultGlobalFilter={restoredGlobalFilter}
                    columns={[
                        { Header: 'N° cuenta', accessor: 'numeroCuenta', width: '14%' },
                        { Header: 'Tipo de tributo', Cell: (props) => { return getDescTipoTributo(props.value); }, accessor: 'codigoTipoTributo', width: '10%' },
                        { Header: 'N° VEP', accessor: 'numeroRecibo', width: '12%', Cell: ({ row }) => (row.original.numeroRecibo > 0) ? getNumeroRecibo_ObjectToIdentificador(row.original) : row.original.codigoBarras },
                        { Header: 'Periodo', accessor: 'periodo', width: '8%', alignCell: 'right' },
                        { Header: 'Cuota', accessor: 'cuota', width: '6%', alignCell: 'right' },
                        { Header: 'Importe 1', accessor: 'importeVencimiento1', Cell: (data) => getFormatNumber(data.value, 2), width: '10%', alignCell: 'right' },
                        { Header: 'Importe 2', accessor: 'importeVencimiento2', Cell: (data) => getFormatNumber(data.value, 2), width: '10%', alignCell: 'right' },
                        { Header: '1° Vto.', accessor: 'fechaVencimiento1', Cell: (data) => getDateToString(data.value), width: '10%' },
                        { Header: '2° Vto.', accessor: 'fechaVencimiento2', Cell: (data) => getDateToString(data.value), width: '10%' },
                    ]}
                />
            )}

            {selectedVep && (
                <ReciboPublicacionModal
                    id={selectedVep.id}
                    onCancel={() => setSelectedVep(null)}
                    onLoteNavigate={prepareReturnToVep}
                />
            )}

        </section>
    </>
}

export default BusquedaReciboPublicacionView
